import { useEffect, useMemo, useState } from "react";
import Modal from "@/components/base/Modal";
import {
  inputClass,
  labelClass,
  ghostButtonClass,
  primaryButtonClass,
} from "@/components/base/fieldStyles";
import type { Expense, ExpenseCategory, Wallet } from "@/types";
import NumberInput from "@/components/base/NumberInput";
import { formatCOP, formatDate } from "@/utils/format";

const CATEGORIES: ExpenseCategory[] = [
  "Donación",
  "Reinversión 25%",
  "Reinversión 30%",
  "Gerencia",
  "Gasto operativo",
  "Caja menor",
];

const WALLETS: Wallet[] = ["Bancolombia", "Daviplata", "Nequi", "Caja Social", "Efectivo"];

interface ExpensesPanelProps {
  expenses: Expense[];
  onAdd: (e: Expense) => void;
  onDelete: (id: string) => void;
}

// Gastos operativos (donación, gerencia, arriendo, servicios...). Las compras de
// mercancía viven en su propio panel, con estado de pago.
const ExpensesPanel = ({ expenses, onAdd, onDelete }: ExpensesPanelProps) => {
  const [view, setView] = useState<"lista" | "concepto">("lista");
  const [open, setOpen] = useState(false);
  const [concept, setConcept] = useState("");
  const [amount, setAmount] = useState(0);
  const [category, setCategory] = useState<ExpenseCategory>("Gasto operativo");
  const [wallet, setWallet] = useState<Wallet>("Efectivo");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [note, setNote] = useState("");
  const [error, setError] = useState("");
  const [confirmId, setConfirmId] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setConcept("");
    setAmount(0);
    setCategory("Gasto operativo");
    setWallet("Efectivo");
    setDate(new Date().toISOString().slice(0, 10));
    setNote("");
    setError("");
  }, [open]);

  const submit = () => {
    if (!concept.trim()) {
      setError("Escribe el concepto del gasto.");
      return;
    }
    if (Number(amount) <= 0) {
      setError("El valor debe ser mayor a cero.");
      return;
    }
    onAdd({
      id: "",
      date: new Date(date).toISOString(),
      concept: concept.trim(),
      category,
      wallet,
      amount: Number(amount) || 0,
      note: note.trim(),
      kind: "operativo",
      status: "pagado",
      paidAmount: Number(amount) || 0,
      fund: null,
    });
    setOpen(false);
  };

  const total = expenses.reduce((a, e) => a + e.amount, 0);

  // Agrupación por concepto para ver de un vistazo cuánto se va en cada rubro.
  const groups = useMemo(() => {
    const map = new Map<string, { label: string; total: number; count: number; lastDate: string }>();
    expenses.forEach((e) => {
      const key = e.concept;
      const current = map.get(key);
      if (current) {
        current.total += e.amount;
        current.count += 1;
        if (e.date > current.lastDate) current.lastDate = e.date;
      } else {
        map.set(key, { label: key, total: e.amount, count: 1, lastDate: e.date });
      }
    });
    return Array.from(map.values()).sort((a, b) => b.total - a.total);
  }, [expenses]);

  return (
    <div className="glass-panel rounded-lg">
      <div className="px-5 py-4 border-b border-accent-500/15 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="w-8 h-8 rounded-md bg-secondary-500/25 text-secondary-200 flex items-center justify-center">
            <i className="ri-wallet-3-line" />
          </span>
          <div>
            <h3 className="editable-title text-base font-bold">Gastos operativos y caja menor</h3>
            <p className="text-[11px] text-foreground-500">
              Total registrado: {formatCOP(total)} · separado de las compras de inventario
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center bg-background-200/60 rounded-full p-1 gap-1">
            {([
              { key: "lista" as const, label: "Lista" },
              { key: "concepto" as const, label: "Por concepto" },
            ]).map((v) => (
              <button
                key={v.key}
                onClick={() => setView(v.key)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap cursor-pointer transition-colors ${
                  view === v.key
                    ? "bg-primary-500 text-foreground-950"
                    : "text-foreground-600 hover:text-foreground-900"
                }`}
              >
                {v.label}
              </button>
            ))}
          </div>
          <button
            onClick={() => setOpen(true)}
            className="px-3.5 py-2 rounded-md bg-primary-500 hover:bg-primary-600 text-foreground-950 text-xs font-semibold whitespace-nowrap cursor-pointer inline-flex items-center justify-center gap-2"
          >
            <i className="ri-add-line" /> Nuevo gasto
          </button>
        </div>
      </div>

      {view === "concepto" ? (
        groups.length === 0 ? (
          <p className="px-5 py-8 text-center text-xs text-foreground-500">
            Aún no hay gastos operativos registrados.
          </p>
        ) : (
          <ul className="divide-y divide-background-300/25">
            {groups.map((g) => (
              <li key={g.label} className="px-5 py-3 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-foreground-950 break-words">{g.label}</p>
                  <p className="text-[10px] text-foreground-500 mt-0.5">
                    {g.count} movimiento(s) · último {formatDate(g.lastDate)}
                  </p>
                </div>
                <span className="text-sm font-bold text-foreground-950 whitespace-nowrap">
                  {formatCOP(g.total)}
                </span>
              </li>
            ))}
          </ul>
        )
      ) : expenses.length === 0 ? (
        <p className="px-5 py-8 text-center text-xs text-foreground-500">
          Aún no hay gastos operativos registrados.
        </p>
      ) : (
        <ul className="divide-y divide-background-300/25">
          {expenses.map((e) => (
            <li key={e.id} className="px-5 py-3 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-foreground-950 break-words">{e.concept}</p>
                <p className="text-[10px] text-foreground-500 mt-0.5">
                  {e.category} · {e.wallet} · {formatDate(e.date)}
                </p>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <span className="text-sm font-bold text-foreground-950 whitespace-nowrap">
                  {formatCOP(e.amount)}
                </span>
                {confirmId === e.id ? (
                  <div className="flex items-center gap-1.5">
                    <button
                      onClick={() => {
                        onDelete(e.id);
                        setConfirmId(null);
                      }}
                      className="px-2.5 py-1 rounded-md bg-primary-500 hover:bg-primary-600 text-foreground-950 text-[11px] font-semibold whitespace-nowrap cursor-pointer"
                    >
                      Sí
                    </button>
                    <button
                      onClick={() => setConfirmId(null)}
                      className="px-2 py-1 rounded-md text-foreground-500 hover:text-foreground-800 text-[11px] font-semibold cursor-pointer"
                    >
                      No
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setConfirmId(e.id)}
                    className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-500 hover:text-primary-200 hover:bg-background-200/60 cursor-pointer"
                    aria-label="Eliminar gasto"
                    title="Eliminar gasto"
                  >
                    <i className="ri-delete-bin-line" />
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="Nuevo gasto operativo"
        icon="ri-wallet-3-line"
        footer={
          <>
            <button onClick={() => setOpen(false)} className={ghostButtonClass}>
              Cancelar
            </button>
            <button onClick={submit} className={primaryButtonClass}>
              <i className="ri-save-3-line" /> Guardar
            </button>
          </>
        }
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <label className={labelClass}>Concepto</label>
            <input
              value={concept}
              onChange={(e) => setConcept(e.target.value)}
              placeholder="Ej: Arriendo, servicios, donación, pauta"
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Categoría</label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value as ExpenseCategory)}
              className={inputClass}
            >
              {CATEGORIES.map((c) => (
                <option key={c} value={c} className="bg-background-100">
                  {c}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelClass}>Billetera / cuenta</label>
            <select
              value={wallet}
              onChange={(e) => setWallet(e.target.value as Wallet)}
              className={inputClass}
            >
              {WALLETS.map((w) => (
                <option key={w} value={w} className="bg-background-100">
                  {w}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelClass}>Valor (COP)</label>
            <NumberInput min={0} value={amount} onChange={setAmount} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Fecha</label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className={inputClass}
            />
          </div>
          <div className="sm:col-span-2">
            <label className={labelClass}>Nota (opcional)</label>
            <input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Detalle adicional"
              className={inputClass}
            />
          </div>
        </div>

        {error && (
          <p className="text-[11px] text-primary-200 mt-3 flex items-center gap-1.5">
            <i className="ri-error-warning-line" /> {error}
          </p>
        )}
      </Modal>
    </div>
  );
};

export default ExpensesPanel;